import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Delete, Check, X } from 'lucide-react';

/**
 * Tastierino numerico a comparsa (touch/TPV) — versione "core" senza dipendenze
 * dal componente Input, così da poter essere usata dentro Input stesso senza
 * creare import circolari.
 */
export const NumericKeypad = ({ open, onClose, initial = '', decimal = true, title, onConfirm, suffix = '' }) => {
  const [val, setVal] = useState(String(initial ?? ''));

  useEffect(() => { if (open) setVal(String(initial ?? '')); }, [open, initial]);

  const press = useCallback((k) => {
    setVal((cur) => {
      if (k === 'back') return cur.slice(0, -1);
      if (k === 'clear') return '';
      if (k === '.') {
        if (!decimal || cur.includes('.')) return cur;
        return cur === '' ? '0.' : cur + '.';
      }
      if (cur.includes('.') && cur.split('.')[1]?.length >= 2) return cur;
      if (cur === '0' && k !== '.') return k;
      return cur + k;
    });
  }, [decimal]);

  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', decimal ? '.' : '00', '0', 'back'];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="numeric-keypad" hideClose>
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="font-semibold text-base">{title || 'Inserisci valore'}</div>
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={onClose}><X className="h-5 w-5" /></Button>
        </div>
        <div className="px-5 pb-2">
          <div className="mt-2 mb-3 h-16 rounded-xl bg-muted flex items-center justify-end px-4 text-3xl font-display font-bold tabular" data-testid="keypad-display">
            {val || '0'}{suffix ? <span className="text-muted-foreground text-xl ml-1.5">{suffix}</span> : null}
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {keys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => press(k === '00' ? '0' : k)}
                data-testid={`keypad-${k === '.' ? 'dot' : k === 'back' ? 'back' : k}`}
                className="h-16 rounded-2xl border border-border bg-card text-2xl font-display font-bold shadow-sm-token transition-transform hover:bg-muted active:scale-[0.96] flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {k === 'back' ? <Delete className="h-6 w-6" /> : k}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2.5 mt-2.5 mb-4">
            <Button type="button" variant="outline" className="h-14 rounded-xl text-base" onClick={() => setVal('')} data-testid="keypad-clear">Cancella</Button>
            <Button type="button" className="h-14 rounded-xl text-base" onClick={() => { onConfirm(val); onClose(); }} data-testid="keypad-confirm"><Check className="h-5 w-5 mr-2" /> OK</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
